import { browserLogger } from '@newfront-insurance/dd-rum';
import JSZip from 'jszip';
import { useCallback, useState } from 'react';

const FOLDER_APPLY_PICKER_ID = 'nova-folder-artifact-apply';

type FileSystemPermissionMode = 'read' | 'readwrite';

export type FolderApplyStatus = 'create' | 'overwrite' | 'conflict';

export type FileSystemWritableFileStreamLike = {
  write: (data: Blob | BufferSource | string) => Promise<void>;
  close: () => Promise<void>;
};

export type FileSystemFileHandleWriteLike = {
  kind: 'file';
  name: string;
  createWritable: () => Promise<FileSystemWritableFileStreamLike>;
};

export type FileSystemDirectoryHandleWriteLike = {
  kind: 'directory';
  name: string;
  getDirectoryHandle: (name: string, options?: { create?: boolean }) => Promise<FileSystemDirectoryHandleWriteLike>;
  getFileHandle: (name: string, options?: { create?: boolean }) => Promise<FileSystemFileHandleWriteLike>;
};

type WindowWithDirectoryPicker = Window & {
  showDirectoryPicker?: (options?: {
    id?: string;
    mode?: FileSystemPermissionMode;
    startIn?:
      | 'desktop'
      | 'documents'
      | 'downloads'
      | 'music'
      | 'pictures'
      | 'videos'
      | FileSystemDirectoryHandleWriteLike;
  }) => Promise<FileSystemDirectoryHandleWriteLike>;
};

type FolderArchiveEntry = {
  path: string;
  pathSegments: string[];
  zipEntry: JSZip.JSZipObject;
};

export type FolderApplyPlanEntry = {
  path: string;
  status: FolderApplyStatus;
  zipEntry: JSZip.JSZipObject;
};

export type FolderApplyPlan = {
  targetFolderName: string;
  targetDirectoryHandle: FileSystemDirectoryHandleWriteLike;
  archiveRootName?: string;
  entries: FolderApplyPlanEntry[];
  createCount: number;
  overwriteCount: number;
  conflictCount: number;
};

export type FolderApplyResult = {
  writtenCount: number;
  createCount: number;
  overwriteCount: number;
};

export class FolderApplyUnsupportedError extends Error {
  constructor() {
    super('Applying folders is not supported in this browser.');
    this.name = 'FolderApplyUnsupportedError';
  }
}

export class EmptyFolderArtifactError extends Error {
  constructor() {
    super('This folder archive does not contain files to apply.');
    this.name = 'EmptyFolderArtifactError';
  }
}

export function isFolderApplySupported() {
  return (
    typeof window !== 'undefined' && typeof (window as WindowWithDirectoryPicker).showDirectoryPicker === 'function'
  );
}

export async function pickFolderApplyDirectory(): Promise<FileSystemDirectoryHandleWriteLike | null> {
  const showDirectoryPicker = (window as WindowWithDirectoryPicker).showDirectoryPicker;

  if (typeof showDirectoryPicker !== 'function') {
    throw new FolderApplyUnsupportedError();
  }

  try {
    return await showDirectoryPicker.call(window, {
      id: FOLDER_APPLY_PICKER_ID,
      mode: 'readwrite',
      startIn: 'documents',
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      browserLogger.info('[Nova] Folder apply picker was cancelled');
      return null;
    }

    throw error;
  }
}

function normalizeZipPath(path: string): string[] | null {
  const normalizedPath = path.replace(/\\/g, '/').replace(/^\uFEFF/, '');

  if (normalizedPath.startsWith('/')) {
    return null;
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  if (segments.some((segment) => segment === '.' || segment === '..')) {
    return null;
  }

  return segments;
}

function getCommonRootFolder(entries: Array<{ pathSegments: string[] }>): string | undefined {
  if (entries.length === 0) {
    return undefined;
  }

  const [firstEntry] = entries;
  const commonRoot = firstEntry.pathSegments[0];
  if (!commonRoot) {
    return undefined;
  }

  return entries.every((entry) => entry.pathSegments[0] === commonRoot && entry.pathSegments.length > 1)
    ? commonRoot
    : undefined;
}

function buildFolderArchiveEntries(zip: JSZip): { entries: FolderArchiveEntry[]; archiveRootName?: string } {
  const rawEntries = Object.values(zip.files)
    .filter((zipEntry) => !zipEntry.dir)
    .map((zipEntry) => {
      const pathSegments = normalizeZipPath(zipEntry.name);
      if (!pathSegments || pathSegments[0] === '__MACOSX') {
        return null;
      }

      return {
        pathSegments,
        zipEntry,
      };
    })
    .filter((entry): entry is { pathSegments: string[]; zipEntry: JSZip.JSZipObject } => entry !== null);

  const archiveRootName = getCommonRootFolder(rawEntries);
  const entries = rawEntries
    .map(({ pathSegments, zipEntry }) => {
      const writablePathSegments = archiveRootName ? pathSegments.slice(1) : pathSegments;
      if (writablePathSegments.length === 0) {
        return null;
      }

      return {
        path: writablePathSegments.join('/'),
        pathSegments: writablePathSegments,
        zipEntry,
      };
    })
    .filter((entry): entry is FolderArchiveEntry => entry !== null);

  return { entries, archiveRootName };
}

function getHandleLookupErrorKind(error: unknown): 'missing' | 'type_mismatch' | 'unknown' {
  if (error instanceof DOMException) {
    if (error.name === 'NotFoundError') return 'missing';
    if (error.name === 'TypeMismatchError') return 'type_mismatch';
  }

  return 'unknown';
}

async function getWriteStatus(
  rootDirectoryHandle: FileSystemDirectoryHandleWriteLike,
  pathSegments: string[],
): Promise<FolderApplyStatus> {
  let directoryHandle = rootDirectoryHandle;

  for (const segment of pathSegments.slice(0, -1)) {
    try {
      directoryHandle = await directoryHandle.getDirectoryHandle(segment, { create: false });
    } catch (error) {
      const errorKind = getHandleLookupErrorKind(error);
      if (errorKind === 'missing') return 'create';
      if (errorKind === 'type_mismatch') return 'conflict';
      throw error;
    }
  }

  const fileName = pathSegments.at(-1);
  if (!fileName) {
    return 'conflict';
  }

  try {
    await directoryHandle.getFileHandle(fileName, { create: false });
    return 'overwrite';
  } catch (error) {
    const errorKind = getHandleLookupErrorKind(error);
    if (errorKind === 'missing') return 'create';
    if (errorKind === 'type_mismatch') return 'conflict';
    throw error;
  }
}

async function getOrCreateParentDirectory(
  rootDirectoryHandle: FileSystemDirectoryHandleWriteLike,
  pathSegments: string[],
): Promise<FileSystemDirectoryHandleWriteLike> {
  let directoryHandle = rootDirectoryHandle;

  for (const segment of pathSegments.slice(0, -1)) {
    directoryHandle = await directoryHandle.getDirectoryHandle(segment, { create: true });
  }

  return directoryHandle;
}

export async function buildFolderApplyPlan({
  archiveBuffer,
  targetDirectoryHandle,
}: {
  archiveBuffer: ArrayBuffer;
  targetDirectoryHandle: FileSystemDirectoryHandleWriteLike;
}): Promise<FolderApplyPlan> {
  const zip = await JSZip.loadAsync(archiveBuffer);
  const { entries: archiveEntries, archiveRootName } = buildFolderArchiveEntries(zip);

  if (archiveEntries.length === 0) {
    throw new EmptyFolderArtifactError();
  }

  const entries = await Promise.all(
    archiveEntries.map(async (entry) => ({
      path: entry.path,
      status: await getWriteStatus(targetDirectoryHandle, entry.pathSegments),
      zipEntry: entry.zipEntry,
    })),
  );

  return {
    targetFolderName: targetDirectoryHandle.name,
    targetDirectoryHandle,
    archiveRootName,
    entries,
    createCount: entries.filter((entry) => entry.status === 'create').length,
    overwriteCount: entries.filter((entry) => entry.status === 'overwrite').length,
    conflictCount: entries.filter((entry) => entry.status === 'conflict').length,
  };
}

export async function applyFolderApplyPlan(plan: FolderApplyPlan): Promise<FolderApplyResult> {
  if (plan.conflictCount > 0) {
    throw new Error('Resolve folder conflicts before applying this folder.');
  }

  for (const entry of plan.entries) {
    const pathSegments = entry.path.split('/').filter(Boolean);
    const fileName = pathSegments.at(-1);

    if (!fileName) {
      continue;
    }

    const parentDirectoryHandle = await getOrCreateParentDirectory(plan.targetDirectoryHandle, pathSegments);
    const fileHandle = await parentDirectoryHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(await entry.zipEntry.async('blob'));
    await writable.close();
  }

  return {
    writtenCount: plan.entries.length,
    createCount: plan.createCount,
    overwriteCount: plan.overwriteCount,
  };
}

async function fetchArchiveBuffer({
  fileUuid,
  resolveArchiveUrl,
}: {
  fileUuid: string;
  resolveArchiveUrl: (fileUuid: string) => Promise<string | null | undefined>;
}) {
  const archiveUrl = await resolveArchiveUrl(fileUuid);

  if (!archiveUrl) {
    throw new Error('Unable to resolve the folder archive URL.');
  }

  const response = await fetch(archiveUrl);
  if (!response.ok) {
    throw new Error('Unable to download the folder archive.');
  }

  return response.arrayBuffer();
}

export function useFolderArtifactApply({
  fileUuid,
  resolveArchiveUrl,
}: {
  fileUuid?: string;
  resolveArchiveUrl: (fileUuid: string) => Promise<string | null | undefined>;
}) {
  const [plan, setPlan] = useState<FolderApplyPlan | null>(null);
  const [isPreparingApply, setIsPreparingApply] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const isSupported = isFolderApplySupported();

  const startApply = useCallback(async () => {
    if (!fileUuid) {
      throw new Error('Folder artifact is not ready yet.');
    }

    const targetDirectoryHandle = await pickFolderApplyDirectory();
    if (!targetDirectoryHandle) {
      return null;
    }

    setIsPreparingApply(true);
    try {
      const archiveBuffer = await fetchArchiveBuffer({ fileUuid, resolveArchiveUrl });
      const nextPlan = await buildFolderApplyPlan({ archiveBuffer, targetDirectoryHandle });
      setPlan(nextPlan);
      return nextPlan;
    } finally {
      setIsPreparingApply(false);
    }
  }, [fileUuid, resolveArchiveUrl]);

  const cancelApply = useCallback(() => {
    setPlan(null);
  }, []);

  const confirmApply = useCallback(async () => {
    if (!plan) {
      throw new Error('Choose a folder before applying this archive.');
    }

    setIsApplying(true);
    try {
      const result = await applyFolderApplyPlan(plan);
      setPlan(null);
      return result;
    } finally {
      setIsApplying(false);
    }
  }, [plan]);

  return {
    isApplying,
    isPreparingApply,
    isSupported,
    plan,
    cancelApply,
    confirmApply,
    startApply,
  };
}
